import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import { BTN, BTN_XS } from '../constants/buttons';
import { APPROVAL_ROLE_LABELS as ROLE_LABELS } from '../constants/statuses';
import { findLongestSequentialSameApproverRun } from '../utils/approvalStepCollapse';
import { FIELD_LABEL_CLASS } from '../constants/inputs';

// 🚨 NO FALLBACK/PLACEHOLDER APPROVERS. The approver picker shows the REAL
// people GET /api/approvals/approvers returns for the active department, or an
// honest empty state — never invented identities. This component used to seed
// `approvers` with a hard-coded list of seven fictional people (Richard Hayes,
// James Wilson, …) shown whenever that fetch came back empty; a department with
// no resolvable approvers then offered a user a chain of people who do not exist
// (backlog M3.7). Do not reintroduce a placeholder list under any name — an
// empty response is `[]`, which renders the empty state below.
const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-700',
  sub_department_lead: 'bg-yellow-100 text-yellow-700',
  user: 'bg-blue-100 text-blue-700',
};


function StepsVisualization({ steps }) {
  // Filter out hidden steps (auto-added buyer final step)
  const visibleSteps = steps ? steps.filter(s => !s.is_hidden) : [];
  return (
    <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-1 items-start">
      {visibleSteps.map((step, i) => (
        <React.Fragment key={step.id || i}>
          {i > 0 && (
            <svg className="w-4 h-4 text-gray-300 flex-shrink-0 rotate-90 md:rotate-0 md:self-auto self-center" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
          <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs w-full md:w-auto ${ROLE_COLORS[step.approver_role] || 'bg-gray-100 text-gray-600'} border-current border-opacity-20`}>
            <span className="font-bold text-gray-400">{i + 1}.</span>
            <span className="font-medium truncate">{step.approver_name}</span>
            <span className="opacity-60 truncate">({ROLE_LABELS[step.approver_role] || step.approver_role})</span>
          </div>
        </React.Fragment>
      ))}
      {/* Show indicator that workflow ends with buyer */}
      {visibleSteps.length > 0 && (
        <>
          <svg className="w-4 h-4 text-gray-300 flex-shrink-0 rotate-90 md:rotate-0 md:self-auto self-center" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs bg-gray-50 text-gray-400 border-gray-200 border-dashed w-full md:w-auto">
            <span className="font-medium">Ready to order</span>
            <span className="opacity-60">(automatic)</span>
          </div>
        </>
      )}
    </div>
  );
}

function ApprovalWorkflowManager({ sectionCollapsed = false }) {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingWorkflowId, setEditingWorkflowId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [approvers, setApprovers] = useState([]);
  // How many assignment-holding members of the active department the server's
  // status filter hid from the approver list (placeholder/deactivated). Drives
  // the honest sparse-state hint below — a department of placeholders otherwise
  // renders a near-empty dropdown that reads as a bug (staging, 2026-08-15).
  const [hiddenApproverCount, setHiddenApproverCount] = useState(0);
  // 'loading' | 'ok' | 'failed' — did GET /api/approvals/approvers actually
  // answer? `approvers.length === 0` is true in ALL THREE states, so it cannot
  // by itself justify the empty state below, which is a claim about the
  // DEPARTMENT rather than about the request. Three states, not a boolean:
  // "not asked yet" must say nothing at all, where "asked and refused" must
  // say something honest.
  const [approversState, setApproversState] = useState('loading');

  // Sub-departments state
  const [subDepartments, setSubDepartments] = useState([]);

  // Form state
  const [selectedAssignment, setSelectedAssignment] = useState(''); // 'subdept:ID' or 'user:Name'
  const [steps, setSteps] = useState([{ approver_name: '', approver_role: '', approver_user_id: null }]);

  const fetchWorkflows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiGet('/api/approvals/workflows');
      if (!res.ok) throw new Error('Failed to fetch workflows');
      const data = await res.json();
      setWorkflows(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await apiGet('/api/approvals/users');
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, []);

  const fetchSubDepartments = useCallback(async () => {
    try {
      const res = await apiGet('/api/sub-departments');
      if (res.ok) {
        const data = await res.json();
        setSubDepartments(data);
      }
    } catch (err) {
      console.error('Failed to fetch sub-departments:', err);
    }
  }, []);

  const fetchApprovers = useCallback(async () => {
    setApproversState('loading');
    try {
      const res = await apiGet('/api/approvals/approvers');
      // A non-ok response (500/401/403) tells us nothing about who may approve
      // in this department — exactly as little as a rejected fetch — so it takes
      // the same path. `if (res.ok)` with no `else` was half of this defect.
      if (!res.ok) throw new Error(`approvers ${res.status}`);
      const data = await res.json();
      // Envelope since 2026-08-15: { approvers: [...], hidden_non_active_count: N }.
      // The legacy bare-array shape is tolerated (a stale server during a
      // deploy window must not blank the picker). Set the real list, empty or
      // not — an empty response renders the empty state, never invented
      // names. (No fallback list exists.)
      const list = Array.isArray(data)
        ? data
        : (Array.isArray(data?.approvers) ? data.approvers : []);
      setApprovers(list);
      const hidden = Array.isArray(data) ? 0 : Number(data?.hidden_non_active_count);
      setHiddenApproverCount(Number.isFinite(hidden) && hidden > 0 ? hidden : 0);
      setApproversState('ok');
    } catch (err) {
      // 🚨 `console.error` alone was the whole handler, and a probe measured
      // /fail|error|reach|offline/i over the WHOLE rendered page as false: the
      // user was told "No members of this department can be named as approvers
      // yet" — a confident statement about their colleagues — because a request
      // failed. Leave the list EMPTY (an invented one is the M3.7 defect above)
      // and record that we do not know.
      console.error('Failed to fetch approvers:', err);
      setApprovers([]);
      setHiddenApproverCount(0);
      setApproversState('failed');
    }
  }, []);

  useEffect(() => {
    fetchWorkflows();
    fetchUsers();
    fetchApprovers();
    fetchSubDepartments();
  }, [fetchWorkflows, fetchUsers, fetchApprovers, fetchSubDepartments]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const resetForm = () => {
    setSelectedAssignment('');
    setSteps([{ approver_name: '', approver_role: '', approver_user_id: null }]);
    setShowAddForm(false);
    setEditingWorkflowId(null);
    setError(null);
  };

  // Clear edit/add state when the section is collapsed externally (matches Productions/Suppliers/Departments pattern)
  useEffect(() => {
    if (sectionCollapsed) {
      setSelectedAssignment('');
      setSteps([{ approver_name: '', approver_role: '', approver_user_id: null }]);
      setShowAddForm(false);
      setEditingWorkflowId(null);
      setError(null);
    }
  }, [sectionCollapsed]);

  const addStep = () => {
    setSteps(prev => [...prev, { approver_name: '', approver_role: '', approver_user_id: null }]);
  };

  const removeStep = (index) => {
    if (steps.length <= 1) return;
    setSteps(prev => prev.filter((_, i) => i !== index));
  };

  const moveStep = (index, direction) => {
    const newSteps = [...steps];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newSteps.length) return;
    [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
    setSteps(newSteps);
  };

  const updateStep = (index, field, value) => {
    setSteps(prev => prev.map((s, i) => {
      if (i !== index) return s;
      if (field === 'approver_name') {
        const approver = approvers.find(a => a.name === value);
        return { ...s, approver_name: value, approver_role: approver ? approver.role : s.approver_role, approver_user_id: approver ? approver.user_id : null };
      }
      return { ...s, [field]: value };
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (editingWorkflowId) {
      // Editing — just update steps
      const validSteps = steps.filter(s => s.approver_name && s.approver_role);
      if (validSteps.length === 0) {
        setError('At least one approval step with an approver is required');
        return;
      }
      try {
        setSaving(true);
        setError(null);
        const res = await apiPut(`/api/approvals/workflows/${editingWorkflowId}`, { steps: validSteps });
        if (!res.ok) throw new Error('Failed to update workflow');
        showSuccess('Workflow updated successfully!');
        resetForm();
        fetchWorkflows();
      } catch (err) {
        setError(err.message);
      } finally {
        setSaving(false);
      }
      return;
    }

    // Creating new — parse assignment type
    if (!selectedAssignment) {
      setError('Please select a sub-department or user');
      return;
    }

    const validSteps = steps.filter(s => s.approver_name && s.approver_role);
    if (validSteps.length === 0) {
      setError('At least one approval step with an approver is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      let payload;
      let label;
      if (selectedAssignment.startsWith('subdept:')) {
        const subDeptId = parseInt(selectedAssignment.replace('subdept:', ''), 10);
        const sd = subDepartments.find(s => s.id === subDeptId);
        payload = { sub_department_id: subDeptId, steps: validSteps };
        label = sd ? sd.name : `Sub-department ${subDeptId}`;
      } else {
        const userName = selectedAssignment.replace('user:', '');
        payload = { user_name: userName, steps: validSteps };
        label = userName;
      }

      const res = await apiPost('/api/approvals/workflows', payload);
      if (!res.ok) throw new Error('Failed to create workflow');
      showSuccess(`Approval workflow created for ${label}!`);

      resetForm();
      fetchWorkflows();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (workflow) => {
    setEditingWorkflowId(workflow.id);
    setSelectedAssignment(workflow.sub_department_id ? `subdept:${workflow.sub_department_id}` : `user:${workflow.user_name}`);
    // Filter out hidden steps (auto-added buyer final step) for editing
    const visibleSteps = workflow.steps.filter(s => !s.is_hidden);
    setSteps(visibleSteps.map(s => ({ approver_name: s.approver_name, approver_role: s.approver_role, approver_user_id: s.approver_user_id || null })));
    setShowAddForm(true);
    setError(null);
  };

  const handleDelete = async (workflowId) => {
    if (!window.confirm('Are you sure you want to delete this approval workflow?')) return;
    try {
      const res = await apiDelete(`/api/approvals/workflows/${workflowId}`);
      if (!res.ok) throw new Error('Failed to delete workflow');
      showSuccess('Workflow deleted successfully!');
      fetchWorkflows();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleActive = async (workflow) => {
    try {
      const res = await apiPut(`/api/approvals/workflows/${workflow.id}`, { is_active: workflow.is_active ? 0 : 1 });
      if (!res.ok) throw new Error('Failed to update workflow');
      showSuccess(`Workflow ${workflow.is_active ? 'deactivated' : 'activated'}!`);
      fetchWorkflows();
    } catch (err) {
      setError(err.message);
    }
  };

  // Get all user names that could have workflows (from orders + known accounts)
  const allUserNames = [...new Set([
    ...users.map(u => u.name),
    ...approvers.map(a => a.name)
  ])].sort();

  // Users who don't already have an active user-based workflow
  const availableUsers = allUserNames.filter(name =>
    !workflows.some(w => w.user_name === name && !w.sub_department_id && w.is_active)
  );

  // Shared workflow add/edit form — rendered at the top when adding, or inline below the workflow being edited
  const workflowForm = (
    <form onSubmit={handleSave} className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg" data-testid="workflow-form">
      <h4 className="text-md font-semibold text-gray-700 dark:text-gray-200 mb-3">
        {editingWorkflowId ? 'Edit Approval Workflow' : 'New Approval Workflow'}
      </h4>

      {/* Assignment Selection - Sub-department or User */}
      {!editingWorkflowId && (
        <div className="mb-4">
          <label className={FIELD_LABEL_CLASS}>
            Assign To <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedAssignment}
            onChange={(e) => setSelectedAssignment(e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
            required
            data-testid="workflow-assign-select"
          >
            <option value="">Select a sub-department or user...</option>
            {subDepartments.length > 0 && (
              <optgroup label="Sub Departments (applies to all users)">
                {subDepartments.map(sd => {
                  const hasWorkflow = workflows.some(w => w.sub_department_id === sd.id && w.is_active);
                  return (
                    <option key={`sd-${sd.id}`} value={`subdept:${sd.id}`} disabled={hasWorkflow}>
                      {sd.name}{hasWorkflow ? ' — Workflow already created' : ''}
                    </option>
                  );
                })}
              </optgroup>
            )}
            <optgroup label="Individual Users (custom override)">
              {availableUsers.map(name => (
                <option key={`u-${name}`} value={`user:${name}`}>{name}</option>
              ))}
            </optgroup>
          </select>
        </div>
      )}

      {editingWorkflowId && (
        <div className="mb-4">
          <label className={FIELD_LABEL_CLASS}>Assigned To</label>
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            {(() => {
              const wf = workflows.find(w => w.id === editingWorkflowId);
              if (wf?.sub_department_id) {
                const sd = subDepartments.find(s => s.id === wf.sub_department_id);
                return sd ? `Sub Dept: ${sd.name}` : wf.user_name;
              }
              return wf?.user_name || '';
            })()}
          </p>
        </div>
      )}

      {/* Approval Steps */}
      <div className="mb-4">
        <label className={FIELD_LABEL_CLASS}>
          Approval Chain <span className="text-red-500">*</span>
        </label>
        {/* Honest empty state — shown when the department has no resolvable
            approvers. This REPLACED a hard-coded list of fictional people that
            used to fill the picker on exactly this path (backlog M3.7). Never
            substitute invented names here — and never re-add the site admin as
            a filler row (removed 2026-08-15 by owner ruling: the only admin is
            SITE admin, who "should not be in the list"; this text is the
            "place holder/note if no one is viable" he asked for instead).
            🚨 GATED ON `approversState === 'ok'`, NOT ON `approvers.length`
            ALONE. This sentence is a claim about the DEPARTMENT's people, and
            it may only be made once the server has actually told us who they
            are. `approvers` is `[]` for the whole first paint too, so a naive
            `!failed` term still renders the false claim on mount — a boolean
            would not have been enough. */}
        {approvers.length === 0 && approversState === 'ok' && (
          <p
            className="text-sm text-gray-500 mb-2"
            data-testid="no-approvers-empty-state"
          >
            No members of this department can be named as approvers yet.
            Activate members in Users, or add people to the department, before
            building an approval chain.
          </p>
        )}
        {/* The request never landed or the server refused it. Say so, and say
            nothing about who can or cannot approve — the file's own rule at the
            top of this component: the picker shows the REAL people the endpoint
            returns "or an honest empty state — never invented identities", and
            an empty state asserted over a failed request is not honest. It
            offers a retry and never suggests activating or adding anybody. */}
        {approversState === 'failed' && (
          <p
            className="text-sm text-red-700 dark:text-red-400 mb-2"
            data-testid="approvers-fetch-error"
          >
            Couldn&rsquo;t load the list of approvers for this department, so it
            may be incomplete.{' '}
            <button
              type="button"
              onClick={fetchApprovers}
              className="font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
              data-testid="approvers-retry"
            >
              Retry
            </button>
          </p>
        )}
        {/* Sparse-state hint (2026-08-15): the server counts the department
            members its status filter hid from the picker. Rendered only when
            that count is > 0, so a genuinely small all-active department gets
            no noise. Explains WHY the dropdown is thin instead of leaving it
            looking broken — and names the remedy (activate them in Users). */}
        {hiddenApproverCount > 0 && (
          <p
            className="text-sm text-amber-700 dark:text-amber-400 mb-2"
            data-testid="hidden-approvers-hint"
          >
            {hiddenApproverCount === 1
              ? '1 member of this department can’t be named as an approver because their account is a placeholder or deactivated — activate them in Users first.'
              : `${hiddenApproverCount} members of this department can’t be named as approvers because their accounts are placeholders or deactivated — activate them in Users first.`}
          </p>
        )}
        <div className="space-y-2">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center gap-2 bg-white dark:bg-gray-700 p-3 rounded-lg border border-gray-200 dark:border-gray-600" data-testid={`workflow-step-${index}`}>
              <span className="text-xs font-bold text-gray-400 w-6 text-center">{index + 1}</span>

              {/* Arrow up/down for reordering */}
              <div className="flex flex-col gap-0.5">
                <button type="button" onClick={() => moveStep(index, -1)} disabled={index === 0}
                  className="text-gray-400 hover:text-gray-700 disabled:opacity-30 text-xs leading-none" title="Move up">
                  &#9650;
                </button>
                <button type="button" onClick={() => moveStep(index, 1)} disabled={index === steps.length - 1}
                  className="text-gray-400 hover:text-gray-700 disabled:opacity-30 text-xs leading-none" title="Move down">
                  &#9660;
                </button>
              </div>

              <select
                value={step.approver_name}
                onChange={(e) => updateStep(index, 'approver_name', e.target.value)}
                className="flex-1 min-w-0 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                required
                data-testid={`step-approver-${index}`}
              >
                <option value="">Select approver...</option>
                {/* KEEPER OPTION (2026-08-15): a stored step can name someone
                    /approvers no longer returns — a placeholder or deactivated
                    member (the status filter hides them), someone de-seated
                    from the department, or the site admin (removed from the
                    picker by owner ruling the same day). Without this option
                    the controlled select has no matching <option> and renders
                    UNSELECTED, so Edit looked like it had not populated the
                    chain (staging report, 2026-08-15). Render the stored value
                    as a disabled option — it displays, but cannot be chosen as
                    a NEW pick here or on any other step (each step only ever
                    renders ITS OWN stored name). The label is deliberately
                    GENERAL ("not currently selectable") because the reasons
                    differ per person and the hint above names the actionable
                    one. This is display only: the step state, the save payload
                    and the server's /validate repair are all unchanged. NOT an
                    invented identity — it is the name the workflow already
                    stores (see M3.7 above).
                    🚨 THE "(not currently selectable)" LABEL IS A CLAIM ABOUT
                    THE PERSON AND IS GATED ON `approversState === 'ok'`. The
                    predicate is "absent from `approvers`", which is true of
                    EVERY stored approver when the fetch failed or has not
                    returned — so on a dropped connection a supervisor was told
                    that every live approver, including ones holding the step
                    right now, could not hold it. When we do not know the list,
                    the keeper renders the stored name plainly and stays
                    selectable: it is the value already on the step, so nothing
                    is asserted about it either way. */}
                {step.approver_name && !approvers.some(a => a.name === step.approver_name) && (
                  approversState === 'ok' ? (
                    <option value={step.approver_name} disabled>
                      {step.approver_name} (not currently selectable)
                    </option>
                  ) : (
                    <option value={step.approver_name}>
                      {step.approver_name}
                    </option>
                  )
                )}
                {approvers.map(a => (
                  <option key={a.name} value={a.name}>{a.label}</option>
                ))}
              </select>

              {step.approver_role && (
                <span className={`hidden sm:inline-block text-xs px-2 py-1 rounded-full whitespace-nowrap ${ROLE_COLORS[step.approver_role] || 'bg-gray-100 text-gray-600'}`}>
                  {ROLE_LABELS[step.approver_role] || step.approver_role}
                </span>
              )}

              <button type="button" onClick={() => removeStep(index)} disabled={steps.length <= 1}
                className="text-red-400 hover:text-red-600 disabled:opacity-30 text-lg font-bold px-1 min-h-[44px] md:min-h-0 inline-flex items-center justify-center" title="Remove step">
                &times;
              </button>
            </div>
          ))}
        </div>

        <button type="button" onClick={addStep}
          className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1 min-h-[44px] md:min-h-0"
          data-testid="add-step-btn">
          <span className="text-lg leading-none">+</span> Add Approval Step
        </button>
      </div>

      <div className="flex gap-3">
        <button type="submit" disabled={saving}
          className={BTN.confirm}
          data-testid="save-workflow-btn">
          {saving ? 'Saving...' : editingWorkflowId ? 'Update Workflow' : 'Create Workflow'}
        </button>
        <button type="button" onClick={resetForm}
          className={BTN.secondary}>
          Cancel
        </button>
      </div>
    </form>
  );

  return (
    <div data-testid="approval-workflows-section">
      {successMessage && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 font-bold">&times;</button>
        </div>
      )}

      {/* INTRO ROW — the instructions and the create control share ONE row.
          They were two stacked blocks (a full-width `justify-end` row holding
          only the button, then the paragraph below it), which put 103px of
          nothing between the top of the section and the first word of the
          text at 1024/1280/1440 (91px at 375). Measured in headless Chromium
          against the compiled stylesheet, identical in light and dark:
          24px outer padding + 24px of the manager's OWN nested `p-6`
          + 38px button + 16px `mb-4` + 1px `border-t` = 103.
          🔑 THE NESTED `p-6` WAS THE HALF NOBODY COULD SEE, and it was drift:
          `AdminSettings.js` already wraps this component in `p-3 md:p-6`, and
          no sibling section (Productions, Departments, Suppliers) carries a
          second one. It also inset the content 24px further than every
          neighbouring section and left the success/error banners above it
          misaligned with the body they describe. Do not reintroduce a padding
          wrapper here — the page owns the inset.
          📱 `flex-col` below `md` so the button never sits beside the text on a
          phone; `md:items-start` so it aligns with the FIRST line of the
          paragraph rather than floating in the middle of it. */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
        {/* `text-gray-500` is deliberately BARE — `index.css:260` delivers its
            dark value with `!important`, so a `dark:` pair here would compile
            and be inert, which reads as explicit dark support and stops the
            next person checking. `min-w-0` is what lets this shrink inside the
            flex row at all (a flex item's default `min-width: auto` pins it at
            its intrinsic content width). */}
        <p className="text-sm text-gray-500 max-w-2xl md:flex-1 min-w-0" data-testid="workflow-intro">
          <span className="font-medium">Decide who signs off an order before it goes out.</span>{' '}
          Pick a sub-department to cover everyone in it, or a single person, then add a step for each
          sign-off you need, in the order they should happen — usually the sub-department lead, then
          the supervisor. An order stops at each step in turn and moves on only once that step is
          approved; a rejection at any step sends it straight back to whoever raised it. Only
          workflows marked Active run — with none set up, orders go straight to whoever runs your
          department&rsquo;s orders.
        </p>
        {!showAddForm && (
          <button
            onClick={() => { setShowAddForm(true); setEditingWorkflowId(null); resetForm(); setShowAddForm(true); }}
            /* `min-h-[44px] md:min-h-0` is a FIX, not decoration: this button
               carried no height floor at all and measured 38px with
               `min-height: auto` at 375, under the house tap target — the odd
               one out in its own file, where the step, toggle, edit and delete
               controls all already carry it. `w-full md:w-auto` follows the
               order pages' rule: a phone WRAPS rather than shrinking a control
               under the floor. */
            className={`${BTN.primary} w-full md:w-auto justify-center min-h-[44px] md:min-h-0 md:flex-shrink-0`}
            data-testid="add-workflow-btn"
          >
            + Add Workflow
          </button>
        )}
      </div>

      {/* Add Workflow Form (Edit form is rendered inline below the workflow being edited) */}
      {showAddForm && !editingWorkflowId && <div className="mb-6">{workflowForm}</div>}

      {/* Existing Workflows */}
      {loading ? (
        <div className="text-center py-4 text-gray-500">Loading workflows...</div>
      ) : workflows.length === 0 ? (
        <div className="text-center py-6 text-gray-400">
          <p className="text-sm">No approval workflows configured yet.</p>
          <p className="text-xs mt-1">Click "+ Add Workflow" to set up an approval chain for a sub-department or user.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {workflows.map(workflow => (
            <React.Fragment key={workflow.id}>
            <div
              className={`border rounded-lg p-4 ${workflow.is_active ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50 opacity-60'}`}
              data-testid={`workflow-${workflow.id}`}>
              {/* Header — info row + actions row (stacks on mobile, inline on desktop) */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
                {/* Info row: name + pills + step count */}
                <div className="flex flex-wrap items-center gap-2 md:gap-3 min-w-0 md:flex-1">
                  <h4 className="font-semibold text-gray-800 truncate min-w-0">
                    {workflow.sub_department_id
                      ? (() => { const sd = subDepartments.find(s => s.id === workflow.sub_department_id); return sd ? sd.name : workflow.user_name; })()
                      : workflow.user_name}
                  </h4>
                  <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${workflow.sub_department_id ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                    {workflow.sub_department_id ? 'Sub Dept' : 'User'}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${workflow.is_active ? 'bg-green-200 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                    {workflow.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{workflow.step_count || workflow.steps?.length || 0} steps</span>
                </div>
                {/* Actions row: stacks below info on mobile (full-width buttons), inline on desktop */}
                <div className="flex items-center gap-2 w-full md:w-auto md:gap-2 md:flex-wrap md:justify-end md:flex-shrink-0">
                  <button onClick={() => handleToggleActive(workflow)}
                    className={`min-h-[44px] md:min-h-0 inline-flex items-center justify-center text-xs px-3 py-1 rounded flex-1 md:flex-none ${workflow.is_active ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                    data-testid={`toggle-workflow-${workflow.id}`}>
                    {workflow.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button onClick={() => handleEdit(workflow)}
                    className={`${BTN_XS.secondary} min-h-[44px] md:min-h-0 inline-flex items-center justify-center flex-1 md:flex-none`}
                    data-testid={`edit-workflow-${workflow.id}`}>
                    Edit
                  </button>
                  <button onClick={() => handleDelete(workflow.id)}
                    className={`${BTN_XS['danger-soft']} min-h-[44px] md:min-h-0 inline-flex items-center justify-center flex-1 md:flex-none`}
                    data-testid={`delete-workflow-${workflow.id}`}>
                    Delete
                  </button>
                </div>
              </div>

              {/* Sequential same-approver WARNING (2026-08-18) — WARNS, does
                  NOT BLOCK: naming the same person twice is a legitimate
                  configuration (the 2026-08-11 buyer-named-mid-chain-and-
                  again-at-the-end use case). Fires only when the workflow
                  gives one person SEQUENTIAL steps — including the
                  auto-appended hidden final step, since the owner ruled it
                  collapses in too. `findLongestSequentialSameApproverRun`
                  is the SAME pure decision `POST /approve` uses to decide
                  what actually collapses; this is display-only. */}
              {(() => {
                const collapseRun = findLongestSequentialSameApproverRun(workflow.steps || []);
                if (!collapseRun) return null;
                return (
                  <div
                    className="mt-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 text-xs text-amber-800 dark:text-amber-300"
                    data-testid={`workflow-collapse-warning-${workflow.id}`}
                  >
                    <span className="font-semibold">{collapseRun.approver_name}</span>
                    {` approves ${collapseRun.steps.length} steps in a row. They'll be approved together in one click. Remove the extra steps if you didn't intend that.`}
                  </div>
                );
              })()}

              {/* Steps visualization */}
              <StepsVisualization steps={workflow.steps} />
            </div>
            {/* Inline edit form — appears directly below the workflow card being edited */}
            {editingWorkflowId === workflow.id && showAddForm && (
              <div className="border-y-2 border-blue-200 dark:border-blue-700 bg-blue-50/40 dark:bg-blue-900/10 p-2 md:p-3 -mt-2 rounded-b-lg">
                {workflowForm}
              </div>
            )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

export default ApprovalWorkflowManager;
